import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  StackDivider,
  Switch,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { DescriptionList, DescriptionListItem, DescriptionListTerm, Loader } from '@netiva/classifieds-ui';

import { useDate } from '@/hooks';
import { CreateApiKeyModal } from './CreateApiKeyModal';

export const ApiKeys: FC = () => {
  const { t } = useTranslation();
  const { formatRange } = useDate();

  const [includeRevoked, setIncludeRevoked] = useState(false);

  const { data: apiKeysData, isLoading: isLoading } = accountApi.useGetApiKeys({ includeRevoked });
  const [revokeApiKey, { isLoading: isRevoking }] = accountApi.useRevokeApiKey();
  const createModal = useDisclosure();

  const apiKeys = useMemo(() => apiKeysData?.apiKeys || [], [apiKeysData]);

  const handleCreate = () => {
    createModal.onOpen();
  };

  const handleRevoke = async (uid: string) => {
    try {
      await revokeApiKey({ uid }).unwrap();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box position="relative">
      <Loader isLoading={isLoading || isRevoking} />
      <VStack align="stretch" divider={<StackDivider />}>
        <Flex justify="space-between" align="center">
          <Button onClick={handleCreate}>{t('api.apiKeys.create')}</Button>

          <FormControl display="flex" alignItems="center" width="auto">
            <FormLabel htmlFor="includeRevoked" mb="0">
              {t('api.apiKeys.includeRevoked')}
            </FormLabel>
            <Switch
              id="includeRevoked"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
            />
          </FormControl>
        </Flex>

        <div>
          <VStack align="stretch" spacing={2}>
            {apiKeys.map((apiKey) => (
              <Flex
                key={apiKey.uid}
                justify="space-between"
                background={apiKey.isRevoked ? 'red.100' : 'gray.100'}
                padding={4}
                borderRadius="default"
              >
                <DescriptionList flex="1 1 auto" background="transparent" p={0}>
                  {apiKey.isRevoked && (
                    <>
                      <DescriptionListTerm>{t('api.apiKeys.isRevoked')}</DescriptionListTerm>
                      <DescriptionListItem></DescriptionListItem>
                    </>
                  )}

                  <DescriptionListTerm>{t('api.apiKeys.uid')}</DescriptionListTerm>
                  <DescriptionListItem>{apiKey.uid}</DescriptionListItem>

                  {apiKey.name && (
                    <>
                      <DescriptionListTerm flexShrink={0}>{t('api.apiKeys.name')}</DescriptionListTerm>
                      <DescriptionListItem>{apiKey.name}</DescriptionListItem>
                    </>
                  )}

                  <DescriptionListTerm flexShrink={0}>{t('api.apiKeys.validFrom')}</DescriptionListTerm>
                  <DescriptionListItem>{formatRange(apiKey.validFrom, apiKey.validTo)}</DescriptionListItem>
                </DescriptionList>
                {!apiKey.isRevoked && (
                  <Button flex="0 0 auto" colorScheme="neutral" onClick={() => handleRevoke(apiKey.uid)}>
                    {t('api.apiKeys.revoke')}
                  </Button>
                )}
              </Flex>
            ))}
          </VStack>
        </div>
      </VStack>
      <CreateApiKeyModal {...createModal} />
    </Box>
  );
};
